/**
 * Generates a task path for a given pull request global ID.
 * @param globalId - The global ID of the pull request.
 * @param sessionId - The ID of the session to be focused on page load.
 * @returns The task path URL, or an empty string if globalId is empty.
 */
export const generateTaskPath = (globalId: string, sessionId?: string) => {
  if (!globalId) {
    return ''
  }

  let taskPath = `/copilot/tasks/pull/${encodeURIComponent(globalId)}`
  if (sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

export interface GenerateTaskPathForRepoOptions {
  /** The repository's name with owner (e.g., "owner/repo"). */
  repositoryNwo?: string
  /** The task ID for direct task linking. */
  taskId?: string
  /** The ID of the session to be focused on page load. */
  sessionId?: string
}

export interface GenerateTaskUrlForRepoOptions extends GenerateTaskPathForRepoOptions {
  /** URLSearchParams (or raw query string) to append to the generated path. */
  searchParams?: URLSearchParams | string
}

/**
 * Generates a task path within a specific repository context.
 * @param options - The options for generating the task path.
 * @returns The task path URL, or an empty string if repository data is unavailable.
 */
export const generateTaskPathForRepo = ({repositoryNwo, sessionId, taskId}: GenerateTaskPathForRepoOptions) => {
  let taskPath = ''
  if (repositoryNwo) {
    if (taskId) {
      taskPath = `/${repositoryNwo}/tasks/${encodeURIComponent(taskId)}`
    }
  } else if (taskId) {
    taskPath = `/copilot/tasks/${encodeURIComponent(taskId)}`
  }

  if (taskPath && sessionId) {
    taskPath += `?session_id=${encodeURIComponent(sessionId)}`
  }

  return taskPath
}

/**
 * Generates a full task URL within a repository context and appends query params.
 * Returns `undefined` when no route can be generated from the provided inputs.
 */
export const generateTaskUrlForRepo = ({
  searchParams,
  ...pathOptions
}: GenerateTaskUrlForRepoOptions): string | undefined => {
  const path = generateTaskPathForRepo(pathOptions)
  if (!path) return undefined

  const query = typeof searchParams === 'string' ? searchParams.replace(/^\?/, '') : searchParams?.toString()
  if (!query) return path
  return `${path}${path.includes('?') ? '&' : '?'}${query}`
}
